import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { communityAPI } from '../lib/api';
import { ArrowLeft, Star, Users, Trophy } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

// Tier styles for badges
const TIER_STYLES = {
  gold: 'bg-gradient-to-br from-yellow-400 to-amber-600 shadow-[0_0_10px_rgba(251,191,36,0.5)]',
  silver: 'bg-gradient-to-br from-gray-300 to-gray-500 shadow-[0_0_10px_rgba(156,163,175,0.4)]',
  bronze: 'bg-gradient-to-br from-orange-400 to-orange-700 shadow-[0_0_10px_rgba(251,146,60,0.4)]'
};

export default function CreatorProfile() {
  const { userId } = useParams();
  useSEO('Creator Profile | JepPrep', 'View a community creator profile, shared boards, and challenge stats.');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      const response = await communityAPI.getCreator(userId);
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load creator profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => (
    <div className="star-rating">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 star ${star <= Math.round(rating) ? 'filled' : ''}`}
          fill={star <= Math.round(rating) ? 'currentColor' : 'none'}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Creator not found</div>
      </div>
    );
  }

  const { creator, boards } = profile;
  const featuredBadges = creator.featured_badges || [];

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center">
            <Link to="/community">
              <Button variant="ghost" size="sm" className="text-gray-300">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <img src={LOGO_URL} alt="JepPrep" className="h-8 ml-4" />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        {/* Profile Header */}
        <div className="text-center mb-12">
          {/* Avatar */}
          <div className="w-28 h-28 mx-auto bg-[hsl(var(--brand-blue-800))] rounded-full flex items-center justify-center overflow-hidden border-4 border-[hsl(var(--brand-gold-500))] mb-4">
            {creator.avatar_url ? (
              <img src={creator.avatar_url} alt={creator.display_name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-4xl text-[hsl(var(--brand-gold-500))] font-display">
                {creator.display_name?.charAt(0).toUpperCase() || '?'}
              </span>
            )}
          </div>
          
          {/* Name & Handle */}
          <h1 className="font-display font-bold text-3xl text-white mb-1">{creator.display_name}</h1>
          {creator.handle && (
            <p className="text-gray-500 text-sm mb-3">@{creator.handle}</p>
          )}
          
          {/* Bio */}
          {creator.bio && (
            <p className="text-gray-400 max-w-lg mx-auto mb-4">{creator.bio}</p>
          )}
          
          {/* Stats */}
          <p className="text-[hsl(var(--brand-gold-400))] mb-6">
            <Trophy className="w-4 h-4 inline mr-1" />
            {creator.boards_published} boards published
          </p>
          
          {/* Featured Badges */}
          {featuredBadges.length > 0 && (
            <div className="mb-6">
              <p className="text-gray-500 text-sm mb-3">Featured Achievements</p>
              <TooltipProvider>
                <div className="flex justify-center gap-3">
                  {featuredBadges.map((badge) => (
                    <Tooltip key={badge.id}>
                      <TooltipTrigger asChild>
                        <div 
                          className={`w-12 h-12 rounded-full flex items-center justify-center cursor-pointer transition-transform hover:scale-110 ${TIER_STYLES[badge.tier] || TIER_STYLES.bronze}`}
                        >
                          <span className="text-xl">{badge.icon}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] text-white">
                        <p className="font-medium">{badge.name}</p>
                        <p className="text-xs text-gray-400 capitalize">{badge.tier} Achievement</p>
                      </TooltipContent>
                    </Tooltip>
                  ))}
                </div>
              </TooltipProvider>
            </div>
          )}
        </div>

        {/* Creator's Boards */}
        <h2 className="font-display font-bold text-xl text-white mb-6">Published Boards</h2>
        {boards.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {boards.map((board) => (
              <Link key={board.id} to={`/community/${board.id}`}>
                <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] card-hover h-full">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-lg text-white mb-2 truncate">{board.title}</h3>
                    <p className="text-gray-400 text-sm mb-3 line-clamp-2">{board.description || 'No description'}</p>
                    <div className="flex items-center gap-2 mb-3">
                      {renderStars(board.community_stats?.rating || 0)}
                      <span className="text-gray-500 text-sm">({board.community_stats?.rating_count || 0})</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span className="capitalize">{board.difficulty}</span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        {board.community_stats?.times_played || 0} plays
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
            <CardContent className="p-12 text-center">
              <p className="text-gray-400">This creator hasn't published any boards yet.</p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
